import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import logo from '../../images/applogomuhriz.png'; // Updated path to your image
import { db } from '../../firebase'; // Ensure this points to your Firebase config

const SalarySlip = ({ salaryData, user }) => {
  const [employeeData, setEmployeeData] = useState(null);

  // Fetch employee details based on user.id
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const employeeRef = doc(db, 'employees', user.id);
        const employeeDoc = await getDoc(employeeRef);

        if (employeeDoc.exists()) {
          setEmployeeData(employeeDoc.data());
        } else {
          console.error('No employee data found!');
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      }
    };

    fetchEmployeeData();
  }, [user.id]);

  if (!employeeData) {
    return <Typography>Loading employee details...</Typography>;
  }

  return (
    <Box sx={{ 
      padding: 2, // Adjusted padding
      maxWidth: '800px', // Adjusted to fit page width
      margin: 'auto', 
      boxShadow: '0 0 2px rgba(0, 0, 0, 0.1)', 
      borderRadius: '8px',
      '@media print': {
        boxShadow: 'none',
        width: '100%',
        margin: 0,
        padding: 0,
        '@page': {
          size: 'A4', // Fixed page size
          margin: '10mm', // Adjust margin to fit content
        },
        '*': {
          boxSizing: 'border-box',
        },
        'body': {
          margin: 0,
          padding: 0,
        },
        '.no-print': {
          display: 'none', // Hide elements with this class in print
        },
        '.salary-slip-table': {
          width: '100%',
          fontSize: '0.75rem', // Reduce font size for printing
        },
        '.salary-slip-table td, .salary-slip-table th': {
          padding: '4px 8px', // Reduce padding
        }
      }
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
        <img src={logo} alt="Company Logo" style={{ width: '100px', height: 'auto', marginRight: '12px' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>MUHRIZ INFOTECH</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Hafiz Mart Building, Main Bazar Khurrianwala Faisalabad, Punjab, Pakistan</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Phone Number: +92-346-7788812</Typography>
          <Typography variant="body2">Email: info@muhriz.com</Typography>
        </Box>
      </Box>

      <Divider sx={{ marginBottom: 2, borderColor: '#ddd' }} />

      {/* Title */}
      <Typography variant="h6" sx={{ marginBottom: 2, textAlign: 'center', fontWeight: 'bold' }}>
        Salary Slip
      </Typography>

      <Divider sx={{ marginBottom: 2, borderColor: '#ddd' }} />

      {/* Employee Details Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, borderBottom: '1px solid #ddd', paddingBottom: 1 }}>
        <Box sx={{ flex: 1, paddingRight: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Employee Information</Typography>
          <Typography variant="body2"><strong>Employee ID:</strong> {employeeData.id}</Typography>
          <Typography variant="body2"><strong>Name:</strong> {employeeData.name}</Typography>
          <Typography variant="body2"><strong>CNIC:</strong> {employeeData.cnic}</Typography>
          <Typography variant="body2"><strong>Address:</strong> {employeeData.address}</Typography>
          <Typography variant="body2"><strong>Email:</strong> {employeeData.email}</Typography>
          <Typography variant="body2"><strong>Position:</strong> {employeeData.position}</Typography>
          <Typography variant="body2">
            <strong>Joining Date:</strong> {new Date(employeeData.joiningDate).toLocaleDateString('en-GB')}
          </Typography>

        </Box>
        <Box sx={{ flex: 1, paddingLeft: 1, textAlign: 'right' }}>
          <img src={employeeData.avatar || logo} alt="Employee Avatar" style={{ width: '100px', height: 'auto', borderRadius: '50%' }} />
        </Box>
      </Box>

      <Divider sx={{ marginBottom: 2, borderColor: '#ddd' }} />

      {/* Salary Details Section */}
      <Typography variant="body2" sx={{ marginBottom: 1 }}>
        <strong>Period:</strong> {salaryData.month}
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: 1 }}>
        <strong>Hours Worked:</strong> {salaryData.monthlyHours}
      </Typography>

      <Divider sx={{ marginBottom: 2, borderColor: '#ddd' }} />

      {/* Salary Breakdown Table */}
      <TableContainer className="salary-slip-table" sx={{ borderRadius: 2, overflow: 'hidden', '@media print': { boxShadow: 'none', border: '1px solid #ddd' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold'}}>Earnings</TableCell>
              <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold'}}>Amount (PKR)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Earnings Section */}
            {salaryData.allowances && salaryData.allowances.length > 0 ? (
              salaryData.allowances.map((allowance, index) => (
                <TableRow key={index}>
                  <TableCell>{allowance.description}</TableCell>
                  <TableCell>{allowance.value.toFixed(2)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No allowances available</TableCell>
              </TableRow>
            )}
            {/* Gross Pay */}
            <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
              <TableCell sx={{fontWeight: 'bold'}}>Gross Pay</TableCell>
              <TableCell sx={{fontWeight: 'bold'}}>{salaryData.grossPay.toFixed(2)}</TableCell>
            </TableRow>
            {/* Separator Row */}
            <TableRow>
              <TableCell colSpan={2} sx={{ borderBottom: '1px solid #ddd' }}></TableCell>
            </TableRow>
            {/* Deductions Section */}
            <>
              {/* Always display the header row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Deductions</TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Amount (PKR)</TableCell>
              </TableRow>

              {/* Check if there are any deductions or leave deduction */}
              {(salaryData.deductions && salaryData.deductions.length > 0) || salaryData.leaveDeduction ? (
                <>
                  {/* Iterate through the deductions array */}
                  {salaryData.deductions && salaryData.deductions.map((deduction, index) => (
                    <TableRow key={index}>
                      <TableCell>{deduction.description}</TableCell>
                      <TableCell>{deduction.value.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}

                  {/* Leave Deduction row */}
                  {salaryData.leaveDeduction ? (
                    <TableRow>
                      <TableCell>Leave Deductions</TableCell>
                      <TableCell>{salaryData.leaveDeduction.toFixed(2)}</TableCell>
                    </TableRow>
                  ) : null}
                </>
              ) : (
                // Display "No deductions available" if neither field has data
                <TableRow sx={{ fontWeight: 'bold' }}>
                  <TableCell>Total Deduction</TableCell>
                  <TableCell>0.00</TableCell>
                </TableRow>
              )}
            </>

            {/* Net Pay */}
            <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
              <TableCell sx={{fontWeight: 'bold'}}>Net Pay</TableCell>
              <TableCell sx={{fontWeight: 'bold'}}>{salaryData.netPay.toFixed(2)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ marginTop: 2, borderColor: '#ddd' }} />

      {/* Footer */}
      <Box sx={{ textAlign: 'center', marginTop: 2 }}>
        <Typography variant="body2" sx={{ color: '#888' }}>This is a computer-generated document and does not require a signature.</Typography>
      </Box>
    </Box>
  );
};

export default SalarySlip;
